import { Button, Heading, Tag, Text } from "@growtherapy/sprout-ui";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { logger } from "../../datadog/logger";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { unreadMessagesCountFamily } from "../../session/state";
import { toast } from "../../toasts";
import {
  parseIdentity,
  participantsAtom,
  RemoteParticipant,
} from "../../video-sdk";
import { useOpenConversationCallback } from "../chat/useOpenConversationCallback";
import { useOpenClientInformationCallback } from "../client-information";
import { ClientMoodWrapper as ClientMood } from "../client-information/ClientMood";
import {
  inSessionPatientInformationAtom,
  ProviderDrawerState,
  sessionConversationSidAtom,
} from "../state";
import { ClientActions } from "./ClientActions";
import { ClientActionsMenu } from "./ClientActionsMenu";
import { useRemoveClientCallback } from "./useRemoveClientCallback";

type ClientsInSessionActionsProps = {
  onRemove: () => void;
  isConversationLoading?: boolean;
  loading?: boolean;
  clientName: string;
  clientNameAnnotation?: React.ReactNode | null;
  unreadMessagesCount?: number;
  patientShortId?: string;
  onChat?: () => void;
  onOpenClientInformationDrawer: () => void;
};

function ClientsInSessionActions({
  onRemove,
  loading,
  clientName,
  clientNameAnnotation,
  patientShortId,
  unreadMessagesCount,
  isConversationLoading,
  onChat,
  onOpenClientInformationDrawer,
}: ClientsInSessionActionsProps) {
  return (
    <ClientActions
      clientName={clientName}
      clientNameAnnotation={clientNameAnnotation}
      actions={
        <div className="flex gap-2 shrink-0">
          <Button
            data-testid={`client-actions.remove:${clientName}`}
            loading={loading}
            use="destructive"
            onClick={onRemove}
          >
            Remove
          </Button>
          <ClientActionsMenu
            visitorName={clientName}
            loading={loading}
            isConversationLoading={isConversationLoading}
            unreadMessagesCount={unreadMessagesCount}
            onChat={onChat}
            patientShortId={patientShortId}
            onOpenClientInformationDrawer={onOpenClientInformationDrawer}
          />
        </div>
      }
    />
  );
}

function ClientsInSessionActionsWrapper({
  participantSid,
  clientName,
  visitorUuid,
}: { participantSid: string; visitorUuid: string } & Pick<
  ClientsInSessionActionsProps,
  "clientName"
>) {
  const { callback: removeClient, loading } = useRemoveClientCallback();

  const getSessionConversationSid = useAtomCallback(
    useCallback((get) => get(sessionConversationSidAtom), []),
  );

  const inSessionVisitor = useAtomValue(inSessionPatientInformationAtom);
  const patientShortId = inSessionVisitor?.patientShortId;
  const sessionConversationSid = getSessionConversationSid();

  const unreadMessagesCount = useAtomValue(
    unreadMessagesCountFamily(sessionConversationSid ?? ""),
  );
  const openConversation = useOpenConversationCallback();
  const openClientInformation = useOpenClientInformationCallback();

  const openClientInformationSection = useCallback(() => {
    if (!patientShortId) {
      logger.error(
        "Missing in-session patient short ID when opening client information drawer",
        { visitorUuid },
      );
      return;
    }
    openClientInformation({ visitorUuid, patientShortId });
  }, [visitorUuid, patientShortId, openClientInformation]);

  const openSessionConversation = () => {
    if (!sessionConversationSid) {
      logger.error("sessionConversationSid not found", {
        participantSid,
        clientName,
      });
      toast.error("Unable to open chat");
      return;
    }
    openConversation(ProviderDrawerState.SESSION_CHAT, sessionConversationSid);
  };

  return (
    <ClientsInSessionActions
      onRemove={() => {
        sendLoggingEvents(TrackingEvents.PROVIDER_CLIENT_REMOVE);
        removeClient(participantSid);
      }}
      clientName={clientName}
      clientNameAnnotation={
        <ClientMood variant="xs" visitorUuid={visitorUuid} />
      }
      unreadMessagesCount={unreadMessagesCount}
      onChat={openSessionConversation}
      loading={loading}
      patientShortId={patientShortId}
      onOpenClientInformationDrawer={openClientInformationSection}
    />
  );
}

export function ClientsInSession({
  clientActionsComponent: ClientActionsComponent,
  participants,
}: {
  clientActionsComponent: typeof ClientsInSessionActionsWrapper;
  participants: RemoteParticipant[];
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <Heading as="h3" variant="md" aria-label="Clients in session">
          In session
        </Heading>
        <Tag use="mauve" data-testid="in-session.count">
          {participants.length}
        </Tag>
      </div>
      {participants.length ? (
        participants.map((participant) => {
          const { id } = parseIdentity(participant.identity);
          return (
            <ClientActionsComponent
              key={participant.sid}
              clientName={participant.displayName}
              participantSid={participant.sid}
              visitorUuid={id}
            />
          );
        })
      ) : (
        <Text>
          Your clients will appear here as soon as they join the session.
        </Text>
      )}
    </>
  );
}

export function ClientsInSessionWrapper() {
  const participants = useAtomValue(participantsAtom);

  return (
    <ClientsInSession
      clientActionsComponent={ClientsInSessionActionsWrapper}
      participants={participants}
    />
  );
}
